
import { Component, Mixins, Vue, Watch } from 'vue-property-decorator';
import VTable from '@/components/table/Table.vue';
import LoadableMixin from '@/mixins/LoadableMixin';
import { Action, Mutation, State } from 'vuex-class';
import TopNavBar from '@/components/app/TopNavBar.vue';
import ListViewMenu from '@/components/app/ListViewMenu.vue';
import AirboltsCarousel from '@/components/airbolts/AirboltsCarousel.vue';
import AirboltsGrid from '@/components/airbolts/AirboltsGrid.vue';
import AirboltsList from '@/components/airbolts/AirboltsList.vue';
import DeviceHistory from '@/components/history/DeviceHistory.vue';
import { UserInfo } from '@/types/user-info';
import { DeviceInfo, SharedDeviceInfo } from '@/types/device-info';
import { AirboltsViewType } from '@/types/store/state';
import DeviceInfoComponent from '@/components/devices/DeviceInfo.vue';
import { actions, mutations } from '@/store/types';
import { LoadDeviceActionType } from '@/types/store/actions';
import { SetDeviceMutationType, SetDevicesMutationType } from '@/types/store/mutations';
import { helpAndSupportURL } from '@/constants/constants';
import api from '@/modules/api';
import { getErrorMessage } from '@/utils/common';


@Component({
    components: {
        VTable,
        TopNavBar,
        ListViewMenu,
        AirboltsCarousel,
        AirboltsGrid,
        AirboltsList,
        DeviceHistory,
        'device-info': DeviceInfoComponent,
    },
    mixins: [LoadableMixin],
})
export default class CouponForm extends Mixins<LoadableMixin>(LoadableMixin) {
    @State('loading')
    readonly loading!: boolean;

    menu = false;
    AirboltsViewType = AirboltsViewType;
    helpAndSupportURL = helpAndSupportURL;
    protected shares: SharedDeviceInfo[] = [];
    expandSearch = false;
    expandRestricted = false;
    private debounceSearch: any = null;
    devices: DeviceInfo[] = [];

    @State('viewType')
    viewType!: AirboltsViewType;

    @State('user')
    user!: UserInfo;

    @State('device')
    current!: DeviceInfo;

    @State('devicesFetched')
    devicesFetched!: boolean;

    @State('searchName')
    searchName!: boolean;

    @State('selectedDeviceType')
    selectedDeviceType!: string;

    @Action(actions.LOAD_DEVICE)
    loadDevice!: LoadDeviceActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    @Mutation(mutations.SET_DEVICES)
    setDevices!: SetDevicesMutationType;

    debounce(val: string) {
        if (this.debounceSearch) {
            clearTimeout(this.debounceSearch);
            this.debounceSearch = null;
        }
        this.debounceSearch = setTimeout(() => {
            this.onSearchChange(val);
        }, 500);
    }

    async onSearchChange(value: string) {
        this.$store.commit(mutations.SET_SEARCH_NAME, { searchName: value == null ? '' : value });
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            //filter devices by type
            const filteredDevices = await api.loadDevices(
                this.user._id as string,
                { page: 0, perPage: 999 },
                this.selectedDeviceType,
                value,
            );
            this.$store.commit(mutations.SET_DEVICES, { devices: filteredDevices });

            // set selected device
            const index = filteredDevices.findIndex((currentdevice) => currentdevice._id === this.current?._id);
            if (filteredDevices.length == 0) {
                this.setCurrentDevice({ device: null });
                this.handleNavigate('');
            } else if (this.current === null || index === -1) {
                this.setCurrentDevice({ device: filteredDevices[0] });
                this.handleNavigate(filteredDevices[0].deviceUUID as string);
            } else {
                this.handleNavigate(this.current?.deviceUUID as string);
            }
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async mounted() {
        if (window.matchMedia('(max-width: 850px)').matches) this.expandRestricted = true;
        this.fetch();
    }

    toggleExpandSearch() {
        this.expandSearch = !this.expandSearch;
    }

    handleNavigate(deviceUUID: string) {
        if (this.$route.path != `/device${deviceUUID ? `/${deviceUUID}` : ''}`) {
            this.$router.push({
                name: 'device-overview',
                params: deviceUUID ? { deviceUUID } : {},
            });
        }
    }

    goBack() {
        const { redirectURL } = this.$route.params;
        this.$router.push({ name: redirectURL ? redirectURL : 'group-devices', params: { ...this.$route.params } });
    }

    protected async fetch() {
        const { groupId } = this.$route.params;
        await this.load(async () => {
            const groupDetails = await api.getDeviceGroupById(groupId);
            this.devices = groupDetails.group.devices;
            this.setDevices({ devices: this.devices });
            this.setCurrentDevice({ device: this.devices[0] });
        }, 'Unable to load coupon');
    }
    @Watch('$route', { immediate: true, deep: true })
    onRouteChange(newRoute: any, oldRoute: any) {
        const newGroupId = newRoute.params.groupId;
        const oldGroupId = oldRoute.params?.groupId;
        if (newGroupId !== oldGroupId) {
            this.fetch();
        }
    }
}
